import DOMPurify from 'dompurify';
import objectHash from 'object-hash';
import { v4 as uuidv4 } from 'uuid';
import { chains } from 'walletconnector';
import { dateMillis, resources } from './constants';
const limitApi = {};
/**
 * use when accessing tracked state[objectName] in useEffect
 * @param {*} api callback should handle all state manipulation and not expect anything in return
 */
export const limitedApi = (lockName, api = () => {}) => {
  if (limitApi[lockName]) return;

  limitApi[lockName] = true;
  api().finally(() => (limitApi[lockName] = false));
};

export const largeText = text => {
  if (Math.random() > 0.5) return text;

  return Array(text.length).fill(text).join(' ');
};

export const numberShort = number => {
  let result;
  const v = Math.round(number).toString();
  if (v.length < 4) {
    result = `${number}`;
  } else if (v.length < 7) {
    result = `${(number / 1000).toFixed(2)}K`;
  } else if (v.length < 10) {
    result = `${(number / 1000000).toFixed(2)}M`;
  } else if (v.length < 13) {
    result = `${(number / 1000000000).toFixed(2)}B`;
  } else {
    result = `${(number / 1000000000000).toFixed(2)}T`;
  }

  result = result.replace(/\.00([KMBT]?)$/, '$1');
  result = result.replace(/(\.\d)0([KMBT]?)$/, '$1$2');

  return result;
};

export const getFileUrl = url => {
  if (!url) return undefined;
  if (url?.startsWith('ipfs://')) return resources.img.ipfs(url);
  return url;
};

// bson is of 12 bytes, so simple append 0x to it
export const bsonIdToBytes12 = bsonObjectId => {
  if (!bsonObjectId || bsonObjectId.length !== 24) return undefined;
  return `0x${bsonObjectId}`;
};
export const getRandomId = () => {
  return uuidv4();
};

export const tweetTemplateParser = (text, dynamicVariables = {}) => {
  let result = text;
  const { mercleReferral, communityReferral, communityEventsPage, openseaLink } = dynamicVariables;
  if (mercleReferral) {
    result = result.replacell('<<mercle_referral>>', mercleReferral);
  }
  if (communityReferral) {
    result = result.replaceAll('<<community_referral>>', communityReferral);
  }
  if (communityEventsPage) {
    result = result.replaceAll('<<community_events_page>>', communityEventsPage);
  }
  if (openseaLink) {
    result = result.replaceAll('<<opensea_link>>', openseaLink);
  }

  result = encodeURIComponent(result);
  return result;
};

export const decodeBase64ToObj = base64Str => {
  if (!base64Str) return undefined;
  return JSON.parse(window.atob(base64Str));
};

export const countdown = (_dateMillis, maxUnit) => {
  let millis = _dateMillis - Date.now();
  const ago = millis < 0;

  millis = Math.abs(millis);

  const W = dateMillis.week_1;
  const D = dateMillis.day_1;
  const H = dateMillis.hour_1;
  const M = dateMillis.min_1;
  const S = 1000;

  const weeks = (millis / W) | 0;
  const days = ((millis - weeks * W) / D) | 0;
  let hours = ((millis - weeks * W - days * D) / H) | 0;
  const mins = ((millis - weeks * W - days * D - hours * H) / M) | 0;
  const secs = ((millis - weeks * W - days * D - hours * H - mins * M) / S) | 0;

  if (maxUnit === 'hour' || maxUnit === 'H') {
    hours = (millis / H) | 0;
  }

  let countdownDateObj = { weeks, days, hours, mins, secs };

  let validUnit = Object.entries(countdownDateObj)?.find(time => time[1] > 0)?.[0];

  return { ago, ...countdownDateObj, validUnit };
};

export const countdownString = dateMillis => {
  const _countdown = countdown(dateMillis);
  let str = [];
  if (_countdown.weeks) str.push(`${_countdown.weeks} Week${_pluralize(_countdown.weeks)}`);
  if (_countdown.days) str.push(`${_countdown.days} Day${_pluralize(_countdown.days)}`);
  if (_countdown.hours) str.push(`${_countdown.hours} Hour${_pluralize(_countdown.hours)}`);
  if (_countdown.mins) str.push(`${_countdown.mins} Min${_pluralize(_countdown.mins)}`);
  if (_countdown.secs) str.push(`${_countdown.secs} Sec${_pluralize(_countdown.secs)}`);
  return { str: str.slice(0, 2), ago: _countdown.ago };
};

const _pluralize = count => (count == 1 ? '' : 's');

export const isValidEmail = email => {
  if (!email) return false;
  return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
};

export const isValidUrl = url => {
  if (!url) return false;
  return /^http(s)?:\/\/.*$/.test(url);
};

export const getElementValue = (id, valueSelector) => {
  const element = document.getElementById(id);
  if (valueSelector) return element?.[valueSelector];
  return element?.value;
};

export const isEmpty = value => {
  if (value == null) return true;
  if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return !value;
};

export const isLightTheme = () => {
  const theme = localStorage.getItem('theme');
  return theme === 'light';
};

export const stringValidator = ({ str, noSpace = false, minLength = 1, maxLength }) => {
  if (!str) {
    return {
      isValid: false,
      errors: ['invalid string'],
    };
  }
  const errors = [];

  if (noSpace && /\s/.test(str)) {
    errors.push('String must not contain spaces.');
  }

  if (str.length < minLength) {
    errors.push(`String must be at least ${minLength} characters long.`);
  }

  if (maxLength !== undefined && str.length > maxLength) {
    errors.push(`String must be no more than ${maxLength} characters long.`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const getChainDetails = chainId => {
  // not doing values cuz it might be a large object, keys list is smaller
  return chains[Object.keys(chains).find(chainName => chains[chainName].id == chainId)];
};

export const hashObject = obj => {
  return objectHash(obj || {});
};
export function getRandomColor(text) {
  // A simple hash function
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ('00' + value.toString(16)).slice(-2);
  }

  // Ensure the color is a valid 6-character hex color
  if (color.length !== 7) {
    color = '#000000'; // default to black if something goes wrong
  }

  return color;
}

export function formatDate(timestamp) {
  const date = new Date(timestamp);
  const day = date.getDate(); // Day of the month
  const month = date.getMonth() + 1; // Months are zero-based
  const year = date.getFullYear().toString().slice(-2); // Last two digits of the year
  let hours = date.getHours(); // Hours (0-23)
  let minutes = date.getMinutes(); // Minutes (0-59)

  // Pad minutes with leading zero if needed
  minutes = minutes < 10 ? '0' + minutes : minutes;

  // Format the date string
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

export function isPastTimestamp(timestamp) {
  let currentTime = Date.now();
  return currentTime > timestamp;
}

export function formatTimestamp(timestamp) {
  let date = new Date(timestamp);
  let formattedDate = date.toLocaleString(); // Converts to a human-readable string
  return formattedDate;
}
export function formatStatsDate(timestamp) {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  //to format Aug 2, 2024
  let date = new Date(timestamp);
  return date.toLocaleDateString('en-US', options);
}

export function formatDecimals(value) {
  return Number(value || 0)?.toFixed(2);
}

export function formatString(str) {
  if (!str) return ''; // Return empty string if input is empty
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function filterObject(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== null && value !== undefined));
}

export function formatNumber(num) {
  if (isNaN(num) || num === null || num === undefined) return '0';
  if (num === 0) return '0';

  let absNum = Math.abs(num);
  let sign = num < 0 ? '-' : '';

  if (absNum < 1) return num.toString(); // Handle small decimals

  if (absNum >= 1e12) return sign + (absNum / 1e12).toFixed(2).replace(/\.00$/, '') + 'T';
  if (absNum >= 1e9) return sign + (absNum / 1e9).toFixed(2).replace(/\.00$/, '') + 'B';
  if (absNum >= 1e6) return sign + (absNum / 1e6).toFixed(2).replace(/\.00$/, '') + 'M';
  if (absNum >= 1e3) return sign + (absNum / 1e3).toFixed(2).replace(/\.00$/, '') + 'K';

  return sign + absNum.toString();
}

export function calculateDelta(initialValue, finalValue) {
  if (typeof initialValue !== 'number' || typeof finalValue !== 'number' || isNaN(initialValue) || isNaN(finalValue)) {
    return null;
  }

  if (initialValue === 0) {
    return finalValue === 0 ? 0 : null;
  }

  const delta = ((finalValue - initialValue) / initialValue) * 100;
  return parseFloat(delta.toFixed(2));
}

export function formatTiers(num) {
  if (num >= 1 && num <= 10) {
    return Math.floor(num);
  } else if (num > 0 && num < 1) {
    return Math.max(0.1, Math.round(num * 10) / 10);
  } else {
    return null;
  }
}

export function truncateEthereumAddress(address) {
  if (/^0x[a-fA-F0-9]{40}$/.test(address)) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }
  return address;
}

export function formatNumberWithCommas(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return '0';

  let numStr = value.toString();
  if (isNaN(numStr) || !isFinite(numStr)) return '0';

  if (typeof value === 'bigint') numStr = value.toString();

  let [integerPart, decimalPart] = numStr.split('.');
  const isNegative = integerPart.startsWith('-');
  if (isNegative) integerPart = integerPart.substring(1);

  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (isNegative) integerPart = '-' + integerPart;

  return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
}

export function getRandomPhrase() {
  const words = {
    nouns: ['dog', 'cat', 'planet', 'guitar', 'ocean', 'wizard', 'robot', 'castle', 'forest', 'meteor'],
    verbs: ['jumps', 'runs', 'flies', 'builds', 'creates', 'destroys', 'sings', 'whispers', 'glows', 'dances'],
    adjectives: ['happy', 'dark', 'shiny', 'mysterious', 'ancient', 'wild', 'sleepy', 'furious', 'gentle', 'brave'],
  };

  function randomWord(type) {
    return words[type][Math.floor(Math.random() * words[type].length)];
  }

  let phrase = [randomWord('adjectives'), randomWord('nouns'), randomWord('verbs'), randomWord('adjectives'), randomWord('nouns')];

  return phrase.join(' ');
}

export function capitalizeFirstLetter(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const reviewTimestamp = ({ timestamp }) => {
  const now = new Date();
  const validTimestamp = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  const date = new Date(validTimestamp);

  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 60) {
    return `${diffMinutes} min`;
  } else if (diffHours < 24) {
    return `${diffHours} hr`;
  } else if (diffDays < 6) {
    return `${diffDays} days`;
  } else {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const currentYear = now.getFullYear();

    if (year < currentYear) {
      return `${day} ${month} ${year}`;
    } else {
      return `${day} ${month}`;
    }
  }
};

export const tweeterTimestamp=({timestamp})=>{
  const now = new Date();
  const validTimestamp = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  const date = new Date(validTimestamp);

  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hr ago`;
  } else if (diffDays < 6) {
    return `${diffDays} days ago`;
  } else {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const currentYear = now.getFullYear();

    if (year < currentYear) {
      return `on ${day} ${month} ${year}`;
    } else {
      return `on ${day} ${month}`;
    }
  }
}

export function santizeHTML(content) {
  return DOMPurify.sanitize(content || '', {
    ALLOWED_TAGS: ['b', 'i', 'p', 'br', 'div'],
    ALLOWED_ATTR: [], // no styles, classes, etc.
  });
}

export function trackClairtyEvent(key, value) {
  if (window.clarity) {
    try {
      window.clarity('set', key, value);
    } catch (e) {
      console.warn('Clarity tagging failed', e);
    }
  }
}


export default {
  limitedApi,
  largeText,
  numberShort,
  getFileUrl,
  bsonIdToBytes12,
  tweetTemplateParser,
  decodeBase64ToObj,

  countdown,
  countdownString,
  isValidEmail,
  isValidUrl,
  getElementValue,

  isEmpty,
  isLightTheme,
  getRandomId,
  isPastTimestamp,
  getChainDetails,
  stringValidator,
  hashObject,
  getRandomColor,
  formatDate,
  formatTimestamp,
  formatDecimals,
  formatStatsDate,
};
